import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import { htmlSafe } from '@ember/template';
import { setupCanvas, roundedRect } from '../../utils/charts/canvas-utils.ts';
import { linearScale, bandScale, niceAxisTicks } from '../../utils/charts/chart-scales.ts';
import { computeLayout, formatAxisValue, formatNumber } from '../../utils/charts/chart-layout.ts';
import { DEFAULT_CHART_COLORS, getThemeColors } from '../../utils/charts/color-palette.ts';
import type { ChartDataPoint, ChartOptions, AxisOptions } from '../../types/charts.ts';

// ---------------------------------------------------------------------------
// HorizontalBarChart – Canvas-rendered horizontal bar chart
// ---------------------------------------------------------------------------

interface HorizontalBarChartSignature {
  Args: ChartOptions &
    AxisOptions & {
      /** Chart data points */
      data: ChartDataPoint[];
      /** Min height (auto-grows with data length, min 300) */
      minHeight?: number;
      /** Whether to sort bars by value descending (default: true) */
      sorted?: boolean;
    };
  Element: HTMLDivElement;
}

const BAR_HEIGHT_PER_ITEM = 28;
const MIN_HEIGHT = 300;

export default class HorizontalBarChart extends Component<HorizontalBarChartSignature> {
  @tracked containerWidth = 400;
  @tracked hoveredIndex: number | null = null;
  @tracked tooltipX = 0;
  @tracked tooltipY = 0;

  get sortedData() {
    const data = [...(this.args.data ?? [])];
    if (this.args.sorted !== false) {
      data.sort((a, b) => b.value - a.value);
    }
    return data;
  }

  get height() {
    const minH = this.args.minHeight ?? MIN_HEIGHT;
    return Math.max(minH, this.sortedData.length * BAR_HEIGHT_PER_ITEM + 60);
  }

  get colors() {
    return this.args.colors ?? DEFAULT_CHART_COLORS;
  }

  get canvasStyle() {
    return htmlSafe(`width: ${this.containerWidth}px; height: ${this.height}px;`);
  }

  get tooltipData() {
    if (this.hoveredIndex === null || !this.sortedData[this.hoveredIndex]) return null;
    const d = this.sortedData[this.hoveredIndex]!;
    return { category: d.category, value: formatNumber(d.value) };
  }

  get tooltipStyle() {
    if (!this.tooltipData) return htmlSafe('display: none;');
    return htmlSafe(`left: ${this.tooltipX}px; top: ${this.tooltipY}px;`);
  }

  private barRects: Array<{ x: number; y: number; w: number; h: number; index: number }> = [];

  installCanvas = modifier((element: HTMLElement) => {
    const canvas = element.querySelector('canvas');
    if (!canvas) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        this.containerWidth = entry.contentRect.width;
        this.draw(canvas);
      }
    });
    observer.observe(element);
    this.containerWidth = element.clientWidth || 400;
    this.draw(canvas);

    const onMove = (e: MouseEvent) => this.handleMouseMove(e, canvas);
    const onLeave = () => {
      this.hoveredIndex = null;
    };
    const onClick = (e: MouseEvent) => this.handleClick(e, canvas);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('click', onClick);

    return () => {
      observer.disconnect();
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('click', onClick);
    };
  });

  draw(canvas: HTMLCanvasElement) {
    const width = this.containerWidth;
    const height = this.height;
    const data = this.sortedData;
    if (!data.length) return;

    // Compute left margin based on longest label
    const longestLabel = Math.max(...data.map((d) => d.category.length));
    const leftMargin = Math.min(150, Math.max(60, longestLabel * 7));

    const layout = computeLayout(width, height, {
      left: leftMargin,
      right: 20,
      top: 20,
      bottom: 30,
    });
    const theme = getThemeColors();

    const ctx = setupCanvas(canvas, width, height);
    ctx.clearRect(0, 0, width, height);

    // Scales
    const maxVal = Math.max(...data.map((d) => d.value), 0);
    const ticks = niceAxisTicks(0, maxVal);
    const xScale = linearScale(
      [0, ticks[ticks.length - 1]!],
      [layout.left, layout.left + layout.innerWidth]
    );
    const categories = data.map((d) => d.category);
    const yScale = bandScale(categories, [layout.top, layout.top + layout.innerHeight], 0.15);

    // Vertical grid lines
    ctx.strokeStyle = theme.grid;
    ctx.lineWidth = 1;
    for (const tick of ticks) {
      const x = Math.round(xScale(tick)) + 0.5;
      ctx.beginPath();
      ctx.moveTo(x, layout.top);
      ctx.lineTo(x, layout.top + layout.innerHeight);
      ctx.stroke();
    }

    // X axis tick labels (bottom)
    const formatFn = this.args.formatValue ?? formatAxisValue;
    ctx.fillStyle = theme.textMuted;
    ctx.font = '11px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (const tick of ticks) {
      ctx.fillText(formatFn(tick), xScale(tick), layout.top + layout.innerHeight + 6);
    }

    // Y axis labels (left)
    ctx.fillStyle = theme.textMuted;
    ctx.font = '11px system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    const bh = yScale.bandwidth();
    for (const cat of categories) {
      const label = cat.length > 20 ? cat.slice(0, 18) + '...' : cat;
      ctx.fillText(label, layout.left - 8, yScale(cat) + bh / 2);
    }

    // Bars
    this.barRects = [];
    data.forEach((d, i) => {
      const y = yScale(d.category);
      const barW = xScale(d.value) - layout.left;
      const color = this.colors[i % this.colors.length]!;

      ctx.fillStyle = this.hoveredIndex === i ? adjustBrightness(color, 1.15) : color;
      roundedRect(ctx, layout.left, y, Math.max(barW, 2), bh, 4);
      ctx.fill();

      this.barRects.push({ x: layout.left, y, w: barW, h: bh, index: i });
    });
  }

  handleMouseMove(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const hit = this.barRects.find((r) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h);

    if (hit) {
      this.hoveredIndex = hit.index;
      this.tooltipX = hit.x + hit.w + 8;
      this.tooltipY = hit.y + hit.h / 2;
      this.draw(canvas);
    } else if (this.hoveredIndex !== null) {
      this.hoveredIndex = null;
      this.draw(canvas);
    }
  }

  handleClick(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const hit = this.barRects.find((r) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h);

    if (hit && this.args.onClick && this.sortedData[hit.index]) {
      this.args.onClick(this.sortedData[hit.index]!);
    }
  }

  <template>
    <div
      data-test-chart
      data-test-chart-type="horizontalBar"
      class="relative w-full"
      {{this.installCanvas}}
      ...attributes
    >
      <canvas style={{this.canvasStyle}}></canvas>

      {{#if this.tooltipData}}
        <div
          data-test-chart-tooltip
          class="absolute z-10 pointer-events-none px-2.5 py-1.5 rounded-lg text-xs shadow-lg border border-gray-200 bg-white text-gray-900 -translate-y-1/2"
          style={{this.tooltipStyle}}
        >
          <div class="font-medium">{{this.tooltipData.category}}</div>
          <div class="text-gray-500">{{this.tooltipData.value}}</div>
        </div>
      {{/if}}
    </div>
  </template>
}

function adjustBrightness(hex: string, factor: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v * factor)));
  return `rgb(${clamp(r)}, ${clamp(g)}, ${clamp(b)})`;
}

export type { HorizontalBarChartSignature };
